import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

const FORM_ACTION = 'https://readdy.ai/api/form/dahac23p14a1h3mr6gsg';

interface FormResponse {
  code?: string;
  message?: string;
  meta?: {
    message?: string;
    detail?: string;
  };
}

type Status = 'idle' | 'submitting' | 'success' | 'error';

export default function ContactForm() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');

  const subjectOptions = t('contact.form.subjectOptions', { returnObjects: true }) as string[];

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') || '').trim();
    if (honeypot) {
      form.reset();
      setStatus('success');
      setFormError('');
      return;
    }
    formData.delete('website_alt');

    setStatus('submitting');
    setFormError('');

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        if (typeof value === 'string') {
          body.append(key, value);
        }
      });

      const response = await fetch(FORM_ACTION, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam =
        String(serverMsg).toLowerCase().includes('spam') ||
        String(responseText).toLowerCase().includes('spam');
      const successCode = parsed?.code === 'OK';

      if (response.ok && successCode && !isSpam) {
        form.reset();
        setStatus('success');
      } else {
        setStatus('error');
        setFormError(serverMsg || t('contact.form.error'));
      }
    } catch {
      setStatus('error');
      setFormError(t('contact.form.error'));
    }
  };

  const inputClass =
    'w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors duration-200 placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-400/30';
  const labelClass = 'mb-2 block text-sm font-medium text-foreground-800';

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
      <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
        {t('contact.form.title')}
      </h2>
      <p className="mt-2 text-sm leading-relaxed text-foreground-600">{t('contact.form.subtitle')}</p>

      <form
        data-readdy-form
        id="sn-contact-form"
        action={FORM_ACTION}
        method="POST"
        onSubmit={handleSubmit}
        className="mt-7 space-y-5"
      >
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <label htmlFor="sn-name" className={labelClass}>
              {t('contact.form.nameLabel')} *
            </label>
            <input
              id="sn-name"
              name="name"
              type="text"
              required
              placeholder={t('contact.form.namePlaceholder')}
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="sn-email" className={labelClass}>
              {t('contact.form.emailLabel')} *
            </label>
            <input
              id="sn-email"
              name="email"
              type="email"
              required
              placeholder={t('contact.form.emailPlaceholder')}
              className={inputClass}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <label htmlFor="sn-phone" className={labelClass}>
              {t('contact.form.phoneLabel')}
            </label>
            <input
              id="sn-phone"
              name="phone"
              type="tel"
              placeholder={t('contact.form.phonePlaceholder')}
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="sn-subject" className={labelClass}>
              {t('contact.form.subjectLabel')}
            </label>
            <select id="sn-subject" name="subject" className={inputClass} defaultValue={subjectOptions[0]}>
              {subjectOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="sn-message" className={labelClass}>
            {t('contact.form.messageLabel')} *
          </label>
          <textarea
            id="sn-message"
            name="message"
            required
            maxLength={500}
            rows={5}
            placeholder={t('contact.form.messagePlaceholder')}
            className={`${inputClass} resize-none`}
          />
        </div>

        <input
          className="sn-form-extra-field"
          type="text"
          name="website_alt"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />

        {status === 'success' && (
          <p role="status" className="flex items-start gap-2 rounded-md border border-accent-300 bg-accent-50 p-4 text-sm text-accent-800">
            <i className="ri-checkbox-circle-line mt-0.5 text-base" />
            {t('contact.form.success')}
          </p>
        )}
        {status === 'error' && (
          <p role="alert" className="flex items-start gap-2 rounded-md border border-primary-300 bg-primary-50 p-4 text-sm text-primary-800">
            <i className="ri-error-warning-line mt-0.5 text-base" />
            {formError || t('contact.form.error')}
          </p>
        )}

        <button
          type="submit"
          disabled={status === 'submitting'}
          className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3.5 text-sm font-semibold text-background-50 transition-colors duration-200 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
        >
          {status === 'submitting' ? t('contact.form.submitting') : t('contact.form.submit')}
          {status !== 'submitting' && <i className="ri-send-plane-line text-base" />}
        </button>
      </form>
    </div>
  );
}